import { useCallback } from 'react';
import { useNodeStore } from '../stores/nodeStore';
import { useProjectStore } from '../stores/projectStore';
import { useTaskStore } from '../stores/taskStore';
import { AppNode, NodeDataByType, NodeType, NodeStatus } from '../types';
import { executeGeneration, GenerationContext, collectDetailPageProductImages, collectDetailPageStyleReferences, collectDetailPageScreenStyleRefItems, buildScreenStyleNotesDeclaration, detailPageRequestsStyleReference, buildStyleReferenceDeclaration, buildPhotorealisticDeclaration, DetailPageScreenStyleRef, normalizeUpscaleResolution, UPSCALE_FACTOR_MAP, UPSCALE_MP_BLOCK_LIMIT, UPSCALE_MP_CONFIRM_LIMIT, estimateUpscaleOutputMp, estimateUpscaleCost } from '../services/generationStrategies';
import { generateApi, getCreditsErrorDetail } from '../services/generateApi';
import { requestOpenMembership } from '../utils/credits';
import { useAuthStore } from '../stores/authStore';
import { toast } from 'sonner';

// 生成串行队列（模块级，跨渲染周期保持）：后端按套餐限制并发（免费版=1，超限提交 403 拒绝），
// 前端按并发度 1 排队，批量生图/手动生成统一入链，上一个任务到终态（含失败）后自动执行下一个。
// executeGeneration 内部轮询至任务终态才 resolve，promise 链即为准确的完成信号
let generationChain: Promise<void> = Promise.resolve();
// 已入队待执行的节点 id：防止排队期间对同一节点重复触发生成
const queuedNodeIds = new Set<string>();
// 已请求取消的节点 id（模块级，跨渲染周期保持）：runGeneration 起点（排队取消拦截跳过）与
// catch 分支（运行取消静默收尾，不走 ERROR 分支弹"生成失败"）共同消费；成功终态兜底清理防竞态污染
const cancelledNodeIds = new Set<string>();
// 积分熔断标志（模块级，跨渲染周期保持）：任一生成任务命中 402（积分不足）后置位，
// 队列中未执行节点全部静默跳过（402 预扣失败不扣费，防的是连环 402 报错与无效请求风暴），
// enqueue 入口同样拦截新请求；刷新页面自动重置。与脚本面板 useScriptPrompts 的
// creditsExhaustedRef 拦截模式保持一致（积分系统开发方案 v1.10 七节 402 统一拦截）
let creditsExhausted = false;

// 取消节点生成（脚本/图片/视频/音频等所有走生成队列的面板共用）：
// 排队态 = 移出队列并立即回 IDLE（任务未提交后端，无需调接口），链轮到时由 cancelledNodeIds 拦截跳过；
// 运行态 = 标记取消 + 调后端 /api/cancel/{taskId}，轮询拿到 cancelled 终态后由 runGeneration catch 静默收尾
export async function cancelNodeGeneration(id: string) {
    const node = useNodeStore.getState().nodes.find(n => n.id === id);
    if (!node) return;
    const { updateNode } = useNodeStore.getState();

    if (queuedNodeIds.has(id)) {
        queuedNodeIds.delete(id);
        cancelledNodeIds.add(id);
        updateNode(id, { error: undefined, progress: undefined, pendingTaskId: undefined, taskId: undefined }, undefined, undefined, NodeStatus.IDLE);
        return;
    }

    cancelledNodeIds.add(id);
    const taskId = node.data.taskId;
    if (taskId) {
        try {
            await generateApi.cancelTask(taskId);
        } catch {
            // 后端取消失败不阻断前端收尾：任务可能刚好完成/失败，由轮询终态兜底
        }
    }
}
// gemini 系降级提示去重（模块级，跨渲染周期保持）：同一节点生命周期内只提示一次
const geminiStyleRefToastShown = new Set<string>();

export function useGenerationOrchestrator(
    handleNodeUpdate: (id: string, data: Partial<AppNode['data']>, size?: { width?: number; height?: number }, title?: string) => void,
    handleAssetGenerated: (type: 'image' | 'video' | 'audio', src: string, title: string, prompt?: string) => void,
) {
    const updateNode = useNodeStore((s) => s.updateNode);
    const currentProjectId = useProjectStore((s) => s.currentProjectId);
    const addTaskWithRequest = useTaskStore((s) => s.addTaskWithRequest);
    const removeTask = useTaskStore((s) => s.removeTask);

    const runGeneration = useCallback(async (id: string, promptOverride?: string) => {
        queuedNodeIds.delete(id);
        // 排队期间被取消：链轮到时直接跳过（节点已在 cancelNodeGeneration 回 IDLE）
        if (cancelledNodeIds.has(id)) {
            cancelledNodeIds.delete(id);
            return;
        }
        // 积分熔断：队列中未执行节点在 402 置位后直接跳过（回 IDLE 保留可重试语义，不标 ERROR）
        if (creditsExhausted) {
            queuedNodeIds.delete(id);
            updateNode(id, { progress: undefined, pendingTaskId: undefined, taskId: undefined }, undefined, undefined, NodeStatus.IDLE);
            return;
        }
        // 节点/输入实时读 store（不用 useEffect 同步的快照 ref）：分块出图是「创建节点后同步触发」，
        // 快照 ref 尚未包含新节点会静默跳过；store 是同步数据源，addNode 后立即可见
        const node = useNodeStore.getState().nodes.find(n => n.id === id);
        if (!node) return;
        if (node.type === NodeType.MEDIA_UPLOAD) return;

        handleNodeUpdate(id, { error: undefined, isManualUpload: false });
        // 立即写 pendingTaskId 占位符 + status=WORKING
        // pendingTaskId 用于持久化"正在生成"标记，即使 taskId 还未从后端返回
        // onTaskCreated 回调会用真实 taskId 覆盖此字段
        // 这样在 500ms 延迟持久化触发时，节点状态已是 WORKING + pendingTaskId（而非 WORKING + 无 taskId）
        // 重新进入项目时即使 taskId 没写入也能通过 pendingTaskId 识别为"需要恢复"
        const pendingTaskId = `pending_${id}_${Date.now()}`;
        updateNode(id, { pendingTaskId, taskId: undefined }, undefined, undefined, NodeStatus.WORKING);

        let currentTaskId: string | null = null;

        try {
            const inputs = node.inputs.map(i => useNodeStore.getState().nodes.find(n => n.id === i)).filter(Boolean) as AppNode[];

            // 详情页节点作为上游时，附加其产品图（面板管理 + 手动连线接入）作为下游生图参考图；
            // 竞品图默认不传递；开启「竞品风格参考出图」的详情页才附加竞品参考图（≤2 张，P2 图片通道）。
            // 产品图节点带 data.image，collectInputImages 天然识别为图生图输入
            const upstreamIds = new Set(inputs.map(n => n.id));
            const detailPageUpstreams = inputs.filter(n => n.type === NodeType.DETAIL_PAGE);
            const detailPageProductNodes = detailPageUpstreams
                .flatMap(dp => collectDetailPageProductImages(dp, useNodeStore.getState().nodes))
                .filter(n => !upstreamIds.has(n.id));
            // P2 gemini 系适配器实际仅单图（后端忽略 inputImages）：检测到不支持时静默降级为纯提示词传导
            // （P1 文字档案仍生效），一次性 toast 告知；竞品参考图排在输入末尾，角色声明按位置区分
            const supportsMultiImage = !(node.data.model || '').toLowerCase().includes('gemini');
            // P2.5 区块级语义映射：上游已切屏（competitorScreens 非空）且本节点区块身份命中 moduleType 时，
            // 竞品参考从整图切换为对应屏切块合成输入节点（仍排输入末尾）；无命中回落整图模式（P2 行为）
            const blockIdentity = [node.title ?? '', (node.data.prompt || '').slice(0, 120)].filter(Boolean).join(' ');
            // P2.5 分块出图节点携带的单元类型（S/D/T）：结构化身份优先于文本关键词（文案标题不可靠）
            const blockUnitType = (node.data as NodeDataByType<NodeType.IMAGE_GENERATOR>).unitType;
            const screenRefItems: DetailPageScreenStyleRef[] = [];
            if (supportsMultiImage) {
                for (const dp of detailPageUpstreams) {
                    const planner = (dp.data as NodeDataByType<NodeType.DETAIL_PAGE>).planner;
                    screenRefItems.push(...collectDetailPageScreenStyleRefItems(planner, blockIdentity, blockUnitType));
                }
            }
            const screenRefUrls = screenRefItems.map(i => i.url);
            const screenRefNodes: AppNode[] = screenRefUrls.map((url, i) => ({
                id: `__dp_screen_ref_${i}`,
                type: NodeType.MEDIA_UPLOAD,
                x: 0,
                y: 0,
                title: '竞品屏切块',
                status: NodeStatus.IDLE,
                data: { image: url },
                inputs: [],
            }));
            const detailPageStyleRefNodes = supportsMultiImage && screenRefNodes.length === 0
                ? detailPageUpstreams
                    .flatMap(dp => collectDetailPageStyleReferences(dp, useNodeStore.getState().nodes))
                    .filter(n => !upstreamIds.has(n.id))
                : [];
            if (!supportsMultiImage && detailPageUpstreams.some(detailPageRequestsStyleReference) && !geminiStyleRefToastShown.has(id)) {
                geminiStyleRefToastShown.add(id);
                toast.info('当前模型不支持多图风格参考，竞品图已降级为文字风格传导');
            }
            const resolvedInputs = [...inputs, ...detailPageProductNodes, ...detailPageStyleRefNodes, ...screenRefNodes];

            // 上游文本收集（脚本节点方案 §3.2 步骤①双通道兜底）：PROMPT_INPUT 走 prompt，
            // TEXT_NODE 等纯文本节点走 textContent；
            // v1.4.56 跳过 scriptAssetRef 资产生成节点（其 prompt 为资产设定描述，不可污染分镜/生图语义）
            const upstreamTexts = resolvedInputs
                .filter(n => !(n?.data as { scriptAssetRef?: unknown } | undefined)?.scriptAssetRef)
                .map(n => {
                    if (!n?.data) return null;
                    const text = n.data.prompt || (n.data as { textContent?: string }).textContent;
                    return typeof text === 'string' && text.trim().length > 0 ? text : null;
                }).filter((t): t is string => !!t);

            let prompt = promptOverride || node.data.prompt || '';
            if (upstreamTexts.length > 0) {
                const combinedUpstream = upstreamTexts.join('\n');
                prompt = prompt ? `${combinedUpstream}\n${prompt}` : combinedUpstream;
            }

            // P2 竞品风格参考角色声明拼到 prompt 尾部（白名单/黑名单/产品保真，防串品与文字污染）；
            // P2.5 屏切块与整图同尾位规则，计数合并；命中屏的版式要点一并注入（图像参考的版式遵循弱，文字要点补足结构约束）；
            // P1 实拍质感锚定（仅详情页上游时注入，防 AI 感/合成感；画布独立生图不污染插画/3D 风格）
            const styleRefDeclaration = buildStyleReferenceDeclaration(detailPageStyleRefNodes.length + screenRefNodes.length);
            const screenNotesDeclaration = buildScreenStyleNotesDeclaration(screenRefItems);
            const photorealisticDeclaration = detailPageUpstreams.length > 0 ? buildPhotorealisticDeclaration() : '';
            const styleTailDeclarations = [screenNotesDeclaration, styleRefDeclaration, photorealisticDeclaration].filter(Boolean).join('\n');
            if (styleTailDeclarations) {
                prompt = `${prompt}\n${styleTailDeclarations}`;
            }

            const ctx: GenerationContext = {
                projectId: currentProjectId,
                onProgress: (nid, data) => handleNodeUpdate(nid, data),
                onAssetGenerated: handleAssetGenerated,
                onTaskCreated: (taskId, nodeId, nodeType, request) => {
                    currentTaskId = taskId;
                    addTaskWithRequest(taskId, nodeId, nodeType, request);
                    // 用真实 taskId 覆盖 pendingTaskId 占位符
                    updateNode(nodeId, { taskId, pendingTaskId: undefined });
                },
            };

            const updates = await executeGeneration(node, resolvedInputs, prompt, ctx);
            // 取消与完成竞态：任务先到终态则以完成为准，清理取消标记防污染下一次生成
            cancelledNodeIds.delete(id);
            handleNodeUpdate(id, updates);
            updateNode(id, { taskId: undefined, pendingTaskId: undefined }, undefined, undefined, NodeStatus.SUCCESS);
            if (currentTaskId) removeTask(currentTaskId);
        } catch (e: any) {
            // 运行中取消：静默回 IDLE，不走 ERROR 分支（不写 error、不弹"生成失败"）
            if (cancelledNodeIds.has(id)) {
                cancelledNodeIds.delete(id);
                updateNode(id, { taskId: undefined, pendingTaskId: undefined, error: undefined, progress: undefined }, undefined, undefined, NodeStatus.IDLE);
                if (currentTaskId) removeTask(currentTaskId);
                return;
            }
            // 积分不足（402）：节点回 IDLE 保留可重试语义（不标 ERROR），置熔断标志跳过队列
            // 后续节点防连环空跑；刷新余额对账 + 引导开通会员（与 useScriptPrompts 拦截模式一致）
            const credits = getCreditsErrorDetail(e);
            if (credits) {
                creditsExhausted = true;
                updateNode(id, { taskId: undefined, pendingTaskId: undefined, error: undefined, progress: undefined }, undefined, undefined, NodeStatus.IDLE);
                if (currentTaskId) removeTask(currentTaskId);
                void useAuthStore.getState().refreshPoints();
                toast.error(`生成中断：${credits.message}`, {
                    description: `本次需 ${credits.need} 积分，当前余额 ${credits.balance}`,
                    duration: 6000,
                });
                requestOpenMembership();
                return;
            }
            handleNodeUpdate(id, { error: e.message });
            updateNode(id, { taskId: undefined, pendingTaskId: undefined }, undefined, undefined, NodeStatus.ERROR);
            if (currentTaskId) removeTask(currentTaskId);
            toast.error('生成失败', { description: e.message, duration: 6000 });
        }
    }, [handleNodeUpdate, updateNode, currentProjectId, handleAssetGenerated, addTaskWithRequest, removeTask]);

    // 实际入队：占位 queuedNodeIds + 挂链（前置校验通过或用户二次确认放行后调用）
    const enqueue = useCallback((id: string, promptOverride?: string) => {
        // 积分熔断：402 置位后拦截新入队请求，防连环报错（开通会员/购买积分或刷新页面后恢复）
        if (creditsExhausted) {
            toast.warning('积分不足，生成已暂停', {
                description: '请开通会员获取更多积分后重试',
                duration: 6000,
            });
            return;
        }
        if (queuedNodeIds.has(id)) return;
        queuedNodeIds.add(id);
        // 链空闲（本次即首位）不占位 QUEUED，避免马上转 WORKING 的状态闪烁；
        // QUEUED 占位不写 pendingTaskId/taskId（任务尚未提交后端），刷新后由 useDataPersistence 重置为 IDLE
        if (queuedNodeIds.size > 1) {
            updateNode(id, {
                error: undefined,
                progress: `排队中（第 ${queuedNodeIds.size} 位）`,
                pendingTaskId: undefined,
                taskId: undefined,
            }, undefined, undefined, NodeStatus.QUEUED);
        }
        const run = generationChain.then(() => runGeneration(id, promptOverride));
        generationChain = run.catch(() => { });
    }, [runGeneration, updateNode]);

    // 同步入队：立即做前置校验（§7.1 高清放大 MP 成本防护）并入链，真正生成等链上轮到
    // （排队期间节点可继续编辑提示词，runGeneration 执行时实时读 store 取最新值）
    const handleNodeAction = useCallback((id: string, promptOverride?: string) => {
        const node = useNodeStore.getState().nodes.find(n => n.id === id);
        if (!node || node.type === NodeType.MEDIA_UPLOAD) return;
        if (queuedNodeIds.has(id)) return;

        // §7.1 前端成本防护：高清放大提交前估算输出 MP（输出MP = 输入宽 × 输入高 × factor² ÷ 1e6）。
        // >512 MP 直接阻断（Replicate 计费上限）；>100 MP 二次确认（toast action 手动放行）。
        // 输入图尺寸缺失（imageDimensions 未回写）时跳过估算，不阻塞主链路
        if (node.type === NodeType.IMAGE_GENERATOR && node.inputs.length > 0) {
            const imgData = node.data as NodeDataByType<NodeType.IMAGE_GENERATOR>;
            if (imgData.generationMode === 'UPSCALE') {
                const inputNode = useNodeStore.getState().nodes.find(n => n.id === node.inputs[0]);
                const dims = inputNode?.data?.imageDimensions;
                if (dims?.width && dims?.height) {
                    const factor = parseInt(UPSCALE_FACTOR_MAP[normalizeUpscaleResolution(imgData.resolution)], 10) || 2;
                    const outputMp = estimateUpscaleOutputMp(dims.width, dims.height, factor);
                    if (outputMp > UPSCALE_MP_BLOCK_LIMIT) {
                        toast.error('图片过大，请降低放大倍数', {
                            description: `预计输出约 ${Math.round(outputMp)} MP，超出 512 MP 计费上限`,
                            duration: 6000,
                        });
                        return;
                    }
                    if (outputMp > UPSCALE_MP_CONFIRM_LIMIT) {
                        toast.warning('预计输出较大，请确认费用', {
                            description: `预计输出约 ${Math.round(outputMp)} MP，费用约 $${estimateUpscaleCost(outputMp).toFixed(2)}/次`,
                            duration: 12000,
                            action: { label: '继续生成', onClick: () => enqueue(id, promptOverride) },
                        });
                        return;
                    }
                }
            }
        }

        enqueue(id, promptOverride);
    }, [runGeneration, updateNode, enqueue]);

    return { handleNodeAction };
}
